import { requireEnv } from '@/lib/env';
import { isDesktop } from '@/lib/platform';
import { type SupportedStorage, createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_KEY;
const domain = import.meta.env.VITE_ALLO_DOMAIN;

requireEnv('Supabase environment variables', supabaseUrl && supabaseKey);

const MAX_COOKIE_CHUNK_SIZE = 3500;
const COOKIE_MAX_AGE = 7776000; // 90 days

function getAuthFromCookie(key: string): string | undefined {
  try {
    const authCookies = document.cookie.split(';').filter((c) => c.trim().startsWith(key));

    if (authCookies.length === 0) {
      return undefined;
    }

    if (authCookies.length === 1) {
      const singleCookieValue = authCookies[0]!.split('=')[1];
      if (singleCookieValue) {
        return atob(singleCookieValue);
      }
    }

    const chunkedCookies = authCookies
      .map((c) => {
        const parts = c.split('=');
        const cookieName = parts[0] ? parts[0].trim() : '';
        const value = parts[1] || '';

        const lastUnderscoreIndex = cookieName.lastIndexOf('_');
        const index =
          lastUnderscoreIndex !== -1
            ? Number.parseInt(cookieName.substring(lastUnderscoreIndex + 1), 10)
            : 0;
        return { index, value };
      })
      .sort((a, b) => a.index - b.index) // important to sort chunks by index
      .map((chunk) => chunk.value)
      .join('');

    return atob(chunkedCookies);
  } catch (error) {
    console.warn('Error retrieving auth cookie', error);
    return undefined;
  }
}

function setAuthToCookie(key: string, value: string) {
  try {
    const encodedToken = btoa(value);

    if (encodedToken.length <= MAX_COOKIE_CHUNK_SIZE) {
      document.cookie = `${key}=${encodedToken}; path=/; domain=${domain}; secure; samesite=strict; max-age=${COOKIE_MAX_AGE}`;
      return;
    }

    const chunks = [];
    for (let i = 0; i < encodedToken.length; i += MAX_COOKIE_CHUNK_SIZE) {
      chunks.push(encodedToken.substring(i, i + MAX_COOKIE_CHUNK_SIZE));
    }

    chunks.forEach((chunk, index) => {
      document.cookie = `${key}_${index}=${chunk}; path=/; domain=${domain}; secure; samesite=strict; max-age=${COOKIE_MAX_AGE}`;
    });
  } catch (error) {
    console.warn('Failed to store auth data in cookies', error);
  }
}

function removeAuthFromCookie(key: string) {
  const cookies = document.cookie.split(';');
  const toRemove = cookies
    .filter((c) => c.trim().startsWith(`${key}`))
    .map((c) => {
      return c.split('=')[0] || '';
    });

  for (const cookieToRemove of toRemove) {
    document.cookie = `${cookieToRemove}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}

const customAuthStorage: {
  auth: {
    storage: SupportedStorage;
  };
} = {
  auth: {
    storage: {
      getItem: (key) => {
        const authTokenFromLS = localStorage.getItem(key);
        const authTokenFromCookie = getAuthFromCookie(key);

        return authTokenFromCookie || authTokenFromLS || null; // take from cookie first
      },
      setItem: (key, value) => {
        localStorage.setItem(key, value); // fallback
        setAuthToCookie(key, value);
      },
      removeItem: (key) => {
        localStorage.removeItem(key);
        removeAuthFromCookie(key);
      },
    },
  },
};

export const supabase = createClient(
  supabaseUrl,
  supabaseKey,
  isDesktop() === false ? customAuthStorage : undefined,
);
